import React from "react";
import { X } from "react-feather";
import { PdfRenderer } from "../pdf-renderer/pdf-renderer";
import { SelectInput } from "../select-input/select-input";
import { documentFileName } from "./download-source";
import {
  isRenderedDocument,
  isUploadEntry,
  needsDocType,
} from "./merge-documents";
import { SixDotsIcon } from "./six-dots-icon";
import type { UsePdfMergeReturn } from "./use-pdf-merge";

type MergeState = UsePdfMergeReturn;

export interface MergeWorkspaceProps {
  merge: MergeState;
  showAddDocument: boolean;
  showUploadButton: boolean;
  addDocumentLabel: string;
  mergingLabel: string;
  previewLabel: string;
  docTypeLabel: string;
  docTypePlaceholder: string;
  compressionError?: string | null;
  onPreviewToCompress: () => void;
}

export function MergeWorkspace({
  merge,
  showAddDocument,
  showUploadButton,
  addDocumentLabel,
  mergingLabel,
  previewLabel,
  docTypeLabel,
  docTypePlaceholder,
  compressionError,
  onPreviewToCompress,
}: MergeWorkspaceProps) {
  const {
    entries,
    selectedIds,
    activeId,
    setActiveId,
    isMerging,
    mergeError,
    activeSrc,
    pdfRef,
    fileInputRef,
    rowRefs,
    sidebarEntries,
    statusMessage,
    canPreview,
    activeEntry,
    activeDocTypeSelectOptions,
    viewerInitialPage,
    viewerNavigatePage,
    viewerToolbar,
    pendingIds,
    activateDocument,
    toggleDocument,
    setDocType,
    moveDocument,
    moveByOffset,
    addFiles,
    removeUpload,
    draggedIdRef,
    pendingViewerPageRef,
    flushViewerNavigation,
    applyPendingViewerNavigation,
    viewerLayoutReadyRef,
    pdfRendererProps,
  } = merge;

  return (
    <>
      <aside className="pdf-merge-panel__sidebar" aria-label="Documents">
        <div className="pdf-merge-panel__list">
          <div className="pdf-merge-panel__rows">
            {sidebarEntries.map((entry) => {
              const isActive = entry.id === activeId;
              const isSelected = selectedIds.has(entry.id);
              const isPending = pendingIds.has(entry.id);
              const isRendered = isRenderedDocument(entry.document);
              const isUntyped = needsDocType(entry);

              return (
                <div
                  key={entry.id}
                  ref={(element) => {
                    if (element) rowRefs.current.set(entry.id, element);
                    else rowRefs.current.delete(entry.id);
                  }}
                  className={[
                    "pdf-merge-panel__row",
                    isActive && "pdf-merge-panel__row--active",
                    !isRendered &&
                      !isPending &&
                      "pdf-merge-panel__row--excluded",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-current={isActive ? "true" : undefined}
                  onClick={() => activateDocument(entry)}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    const draggedId =
                      draggedIdRef.current ||
                      event.dataTransfer.getData("text/plain");
                    if (draggedId) moveDocument(draggedId, entry.id);
                    draggedIdRef.current = null;
                  }}
                >
                  {
                    <input
                      className="pdf-merge-panel__checkbox"
                      type="checkbox"
                      checked={isSelected}
                      disabled={isUntyped}
                      aria-label={`Include ${entry.document.doc_name}`}
                      title={
                        isUntyped
                          ? `Select a document type for ${entry.document.doc_name} first`
                          : undefined
                      }
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => toggleDocument(entry)}
                    />
                  }
                  <button
                    type="button"
                    className="pdf-merge-panel__document-button"
                    tabIndex={isActive ? 0 : -1}
                    onClick={(event) => {
                      event.stopPropagation();
                      activateDocument(entry);
                    }}
                    title={
                      isUntyped
                        ? `${entry.document.doc_name} (needs a document type)`
                        : isPending
                        ? `${entry.document.doc_name} (added on the next merge)`
                        : isRendered
                        ? entry.document.doc_name
                        : `${entry.document.doc_name} (not in the current view)`
                    }
                  >
                    <span className="pdf-merge-panel__document-name">
                      {entry.document.doc_name}
                    </span>
                  </button>
                  {isUploadEntry(entry) && !isSelected && (
                    <button
                      type="button"
                      className="pdf-merge-panel__pending-remove"
                      aria-label={`Remove ${entry.document.doc_name}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        removeUpload(entry.id);
                      }}
                    >
                      <X size={12} aria-hidden="true" />
                    </button>
                  )}
                  <button
                    type="button"
                    className="pdf-merge-panel__drag-handle"
                    draggable
                    aria-label={`Reorder ${entry.document.doc_name}`}
                    title="Drag to reorder. Use Alt+Arrow keys for keyboard reordering."
                    onClick={(event) => event.stopPropagation()}
                    onDragStart={(event) => {
                      draggedIdRef.current = entry.id;
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", entry.id);
                    }}
                    onDragEnd={() => {
                      draggedIdRef.current = null;
                    }}
                    onKeyDown={(event) => {
                      if (!event.altKey) return;
                      if (event.key === "ArrowUp") {
                        event.preventDefault();
                        moveByOffset(entry.id, -1);
                      } else if (event.key === "ArrowDown") {
                        event.preventDefault();
                        moveByOffset(entry.id, 1);
                      }
                    }}
                  >
                    <SixDotsIcon />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
        {statusMessage && (
          <p
            className={[
              "pdf-merge-panel__status",
              mergeError && "pdf-merge-panel__status--error",
            ]
              .filter(Boolean)
              .join(" ")}
            role={mergeError ? "alert" : "status"}
          >
            {statusMessage}
          </p>
        )}
      </aside>

      <section className="pdf-merge-panel__preview" aria-label="PDF preview">
        {activeEntry && (
          <div className="pdf-merge-panel__file-bar">
            <div
              className="pdf-merge-panel__file-name"
              title={documentFileName(activeEntry.document.doc_name)}
            >
              {documentFileName(activeEntry.document.doc_name)}
            </div>
            {isUploadEntry(activeEntry) && (
              <SelectInput
                className={[
                  "pdf-merge-panel__doc-type",
                  needsDocType(activeEntry) &&
                    "pdf-merge-panel__doc-type--untyped",
                ]
                  .filter(Boolean)
                  .join(" ")}
                label={`${docTypeLabel} for ${activeEntry.document.doc_name}`}
                placeholder={docTypePlaceholder}
                value={activeEntry.document.doc_type}
                options={activeDocTypeSelectOptions}
                onChange={(value) => setDocType(activeEntry, value)}
              />
            )}
          </div>
        )}
        <div className="pdf-merge-panel__viewer">
          <PdfRenderer
            {...pdfRendererProps}
            ref={pdfRef}
            src={activeSrc}
            toolbar={viewerToolbar}
            height="100%"
            width="100%"
            initialPage={viewerInitialPage}
            page={viewerNavigatePage ?? pdfRendererProps?.page}
            onDocumentLoad={(info) => {
              pdfRendererProps?.onDocumentLoad?.(info);
              viewerLayoutReadyRef.current = false;
            }}
            onLayoutReady={(info) => {
              pdfRendererProps?.onLayoutReady?.(info);
              viewerLayoutReadyRef.current = true;
              applyPendingViewerNavigation();
            }}
            onPageChange={(pageInfo) => {
              pdfRendererProps?.onPageChange?.(pageInfo);
              flushViewerNavigation(pageInfo.currentPage);
              if (pendingViewerPageRef.current != null) return;
              const visible = entries.find(
                (entry) =>
                  isRenderedDocument(entry.document) &&
                  pageInfo.currentPage >= entry.document.start_page &&
                  pageInfo.currentPage <= entry.document.end_page
              );
              if (visible) setActiveId(visible.id);
            }}
          />
        </div>
      </section>

      <footer className="pdf-merge-panel__footer">
        {compressionError && (
          <p className="pdf-merge-panel__compress-error" role="alert">
            {compressionError}
          </p>
        )}
        <div className="pdf-merge-panel__footer-start">
          {showAddDocument && showUploadButton && (
            <button
              type="button"
              className="pdf-merge-panel__upload-button"
              onClick={() => fileInputRef.current?.click()}
            >
              {addDocumentLabel}
            </button>
          )}
        </div>
        <div className="pdf-merge-panel__footer-end">
          <button
            type="button"
            className="pdf-merge-panel__preview-button"
            disabled={!canPreview}
            onClick={onPreviewToCompress}
          >
            {isMerging ? mergingLabel : previewLabel}
          </button>
        </div>
      </footer>

      <input
        ref={fileInputRef}
        className="pdf-merge-panel__file-input"
        type="file"
        accept="application/pdf,.pdf,image/jpeg,.jpg,.jpeg,image/png,.png"
        multiple
        tabIndex={-1}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          event.target.value = "";
          if (files.length > 0) addFiles(files);
        }}
      />
    </>
  );
}
